/**
 * ============================================================================
 * The course-section builder (H2 v2, Path A)
 * ============================================================================
 * A coordinator builds an NCCCS-anchored course section here: CCL identity,
 * delivery method, the meeting schedule, and — visible the whole time — what
 * that schedule does to the census date and the reporting period.
 *
 * TWO THINGS THIS SCREEN DELIBERATELY DOES NOT DO.
 *
 * It does not compute the census. That comes from the server, which asks
 * Postgres. A number a coordinator plans around must be the number the record
 * will carry.
 *
 * It does not disable Publish when the hours do not reconcile. The database
 * refuses the publish (the M1 trigger), and it is the database's refusal the
 * coordinator should see. A greyed-out button with no explanation teaches
 * nothing; a clear message from the actual enforcement point teaches the rule.
 * The panel above warns long before they get there, so the refusal is never a
 * surprise — just never a lie either.
 * ============================================================================
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from '../components/ErrorBanner.jsx';
import SessionsPanel from '../components/SessionsPanel.jsx';
import SectionItemsPanel from '../components/SectionItemsPanel.jsx';
import MaterialsShelf from '../components/MaterialsShelf.jsx';
import DeckEditor from '../components/DeckEditor.jsx';
import Field from '../components/Field.jsx';
import Select from '../components/Select.jsx';
import MeetingScheduleEditor from '../components/MeetingScheduleEditor.jsx';
import CensusPreview from '../components/CensusPreview.jsx';
import {
  DELIVERY_METHODS,
  SCHEDULE_TYPES,
  STATUS_LABELS,
  emptySection,
  toForm,
  toPayload,
} from '../lib/sections.js';

export function SectionBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();

  const isNew = id === 'new';
  const [form, setForm] = useState(() => emptySection(profile?.institution?.id ?? ''));
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    apiFetch('/api/v1/courses?active=1')
      .then((list) => setCourses(list ?? []))
      .catch(() => setCourses([]));
  }, []);
  const [section, setSection] = useState(null);
  const [versions, setVersions] = useState([]);
  const [institutions, setInstitutions] = useState([]);

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const canPublish = profile?.permissions?.includes('class.publish') ?? false;
  const isPublished = section?.status === 'published';

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  // --- load ----------------------------------------------------------------
  useEffect(() => {
    let active = true;

    apiFetch('/api/v1/institutions')
      .then((data) => active && setInstitutions(data ?? []))
      .catch(() => active && setInstitutions([]));

    if (isNew) {
      setForm(emptySection(profile?.institution?.id ?? ''));
      setSection(null);
      setVersions([]);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    apiFetch(`/api/v1/classes/${id}`)
      .then((data) => {
        if (!active) return;
        setSection(data.section);
        setVersions(data.versions ?? []);
        setForm(toForm(data.section));
      })
      .catch((err) => active && setError(err))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [id, isNew, profile?.institution?.id]);

  // --- live census preview -------------------------------------------------
  // Debounced, and stale responses are dropped by sequence number: typing an
  // end time fires several requests and they can return out of order, which
  // would otherwise leave the panel showing the census for a schedule that no
  // longer exists.
  const scheduleKey = useMemo(
    () => JSON.stringify([form.section_schedule_type, form.meeting_schedule]),
    [form.section_schedule_type, form.meeting_schedule]
  );
  const previewSeq = useRef(0);

  useEffect(() => {
    const seq = ++previewSeq.current;
    setPreviewLoading(true);

    const timer = setTimeout(() => {
      apiFetch('/api/v1/classes/census-preview', {
        method: 'POST',
        body: JSON.stringify({
          section_schedule_type: form.section_schedule_type || null,
          meeting_schedule: form.meeting_schedule ?? [],
        }),
      })
        .then((data) => {
          if (seq !== previewSeq.current) return; // a newer edit has overtaken this
          setPreview(data);
          setPreviewError(null);
        })
        .catch((err) => {
          if (seq !== previewSeq.current) return;
          setPreviewError(err);
        })
        .finally(() => {
          if (seq === previewSeq.current) setPreviewLoading(false);
        });
    }, 350);

    return () => clearTimeout(timer);
    // scheduleKey covers both dependencies; listing it alone keeps the effect
    // from re-firing on unrelated field edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleKey]);

  // --- actions -------------------------------------------------------------
  async function saveDraft(event) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setSaving(true);

    try {
      const payload = toPayload(form);

      if (isNew) {
        const created = await apiFetch('/api/v1/classes', {
          method: 'POST',
          body: JSON.stringify({ ...payload, institution_id: form.institution_id }),
        });
        setNotice('Draft saved.');
        navigate(`/sections/${created.id}`, { replace: true });
      } else {
        const updated = await apiFetch(`/api/v1/classes/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
        setSection(updated);
        setForm(toForm(updated));
        setNotice('Draft saved.');
      }
    } catch (err) {
      setError(err);
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    setError(null);
    setNotice(null);
    setPublishing(true);

    try {
      const result = await apiFetch(`/api/v1/classes/${id}/publish`, { method: 'POST' });
      setSection(result.section);
      setForm(toForm(result.section));
      setNotice(
        `Published as version ${result.published.version}. ` +
          `This version is now permanent — sessions and completion records will reference it.`
      );

      const detail = await apiFetch(`/api/v1/classes/${id}`);
      setVersions(detail.versions ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setPublishing(false);
    }
  }

  const scheduleType = SCHEDULE_TYPES.find((t) => t.value === form.section_schedule_type);

  if (loading) {
    return (
      <p className="muted" aria-busy="true">
        Loading section…
      </p>
    );
  }

  return (
    <>
      <p className="crumb">
        <Link to="/sections">← All sections</Link>
      </p>

      <h1>{isNew ? 'New course section' : form.title || 'Course section'}</h1>
      <p className="muted">
        {isNew
          ? 'Everything here lands on the Official Attendance Record, so it is worth getting right before you publish.'
          : `${STATUS_LABELS[section?.status] ?? section?.status ?? 'Draft'}${
              section?.version ? ` · version ${section.version}` : ''
            }`}
      </p>

      <ErrorBanner error={error} />
      {notice && (
        <p className="banner banner--success" role="status">
          {notice}
        </p>
      )}

      {isPublished && (
        <>
          <p className="callout callout--neutral">
            This section is published. Editing it here and publishing again creates a new version —
            the published one stays exactly as it was, because completed sessions reference it.
          </p>
          {/* Enrolment only appears once published: a person can only be enrolled
              against a published version, which is what stops the section
              changing underneath someone who has already registered. */}
          <p className="toolbar">
            <Link className="btn btn--auto" to={`/sections/${id}/enrollments`}>
              Enrolments
            </Link>
            <Link className="btn btn--auto" to={`/sections/${id}/record`}>
              Official Attendance Record
            </Link>
            <Link className="btn btn--auto" to={`/sections/${id}/packet`}>
              Closing packet
            </Link>
          </p>

          {/* The way in to actually running this section. Everything below the
              section itself — the check-in code, the live roster, the presenter,
              certification, completions — hangs off a session, and until now
              nothing in the interface linked to one. */}
          <SessionsPanel classId={id} sectionStatus={section?.status} />

          {/* What the room is asked. Items attach to the published version, so
              this only appears once there is one. */}
          <SectionItemsPanel classId={id} sectionStatus={section?.status} />

          {/* The slides Present shows, built around those items (M45). */}
          <DeckEditor classId={id} canEdit={profile?.permissions?.includes('class.manage') ?? false} />
        </>
      )}

      {/* The shelf (M26): slides, video links, handouts — attached here once,
          one tap for the instructor on Run it and Present. Belongs to the
          section, not a version, so it is here whether or not it is published. */}
      {id && (
        <section className="panel" aria-labelledby="materials-heading">
          <h2 id="materials-heading">Materials</h2>
          <p className="muted">
            What the instructor should have to hand when the class runs: the slides, a video, the
            handout, the skill sheet. Instructors open these from the session screens.
          </p>
          <MaterialsShelf classId={id} manage />
        </section>
      )}

      <CensusPreview
        preview={preview}
        approvedHours={form.state_approved_hours === '' ? null : Number(form.state_approved_hours)}
        loading={previewLoading}
        error={previewError}
      />

      <form onSubmit={saveDraft}>
        <section className="panel" aria-labelledby="identity-heading">
          <h2 id="identity-heading">Course identity</h2>
          <p className="muted">
            The State's coding for this section. These are the fields an auditor matches against
            the Combined Course Library.
          </p>

          {isNew && (
            <Select
              label="Agency"
              value={form.institution_id}
              onChange={(v) => set({ institution_id: v })}
              options={institutions.map((i) => ({ value: i.id, label: i.name }))}
              placeholder="Choose the agency this section belongs to…"
              hint="Set once, when the section is created."
            />
          )}

          <div className="field-row field-row--3">
            <Field
              label="Course prefix"
              value={form.course_prefix}
              onChange={(v) => set({ course_prefix: v })}
              required={false}
              placeholder="EMS"
            />
            <Field
              label="Course number"
              value={form.course_number}
              onChange={(v) => set({ course_number: v })}
              required={false}
              placeholder="3020"
            />
            <Field
              label="Section number"
              value={form.section_number}
              onChange={(v) => set({ section_number: v })}
              required={false}
              placeholder="01"
            />
          </div>

          {/* required={false} optional={false} throughout: a draft may be saved
              half-finished, but none of these are optional — they are needed to
              publish. See the note in Field.jsx. */}
          <Field
            label="CCL course code"
            required={false}
            optional={false}
            value={form.ccl_course_code}
            onChange={(v) => set({ ccl_course_code: v })}
            hint="The code as it appears in the Combined Course Library. Needed to publish."
            placeholder="EMS-3020"
          />

          <Field
            label="Official CCL title"
            required={false}
            optional={false}
            value={form.official_ccl_title}
            onChange={(v) => set({ official_ccl_title: v })}
            hint="Word for word from the CCL, even where it differs from what everyone calls the class. Needed to publish."
          />

          <Field
            label="Working title"
            value={form.title}
            onChange={(v) => set({ title: v })}
            hint="What staff will see in Attest. This one can be the everyday name."
          />

          <Field
            label="Topic"
            value={form.topic}
            onChange={(v) => set({ topic: v })}
            required={false}
          />

          {/* The catalog link (M27): what completing this section is worth and
              for how long. Optional - a one-off in-service earns hours and
              confers no standing. */}
          <Select
            label="Counts toward"
            value={form.course_id}
            onChange={(v) => set({ course_id: v })}
            options={courses.map((c) => ({
              value: c.id,
              label: `${c.code} - ${c.name}${c.valid_months ? ` (good for ${c.valid_months % 12 === 0 ? `${c.valid_months / 12} yr` : `${c.valid_months} mo`})` : ''}`,
            }))}
            placeholder="Not in the catalog - hours only"
            required={false}
            hint="Pick the catalog course this section renews. The Tracker uses it to say when each person is due."
          />
        </section>

        <section className="panel" aria-labelledby="delivery-heading">
          <h2 id="delivery-heading">Hours &amp; delivery</h2>

          <div className="field-row">
            <Field
              label="State Board–approved hours"
              type="number"
              step="0.25"
              min="0"
              required={false}
              optional={false}
              value={form.state_approved_hours}
              onChange={(v) => set({ state_approved_hours: v })}
              hint="From the CCL. The meeting schedule must add up to exactly this."
            />
            <Field
              label="CE hours awarded"
              type="number"
              step="0.25"
              min="0"
              required={false}
              optional={false}
              value={form.ce_hours}
              onChange={(v) => set({ ce_hours: v })}
              hint="What a participant earns for completing it."
            />
          </div>

          <Field
            label="Passing threshold (%)"
            type="number"
            step="1"
            min="0"
            max="100"
            required={false}
            optional={false}
            value={form.passing_threshold}
            onChange={(v) => set({ passing_threshold: v })}
          />

          <Select
            label="Delivery method"
            required={false}
            optional={false}
            value={form.delivery_method}
            onChange={(v) => set({ delivery_method: v })}
            options={DELIVERY_METHODS}
            hint="Printed on the Official Attendance Record. Needed to publish."
          />

          <Select
            label="Section schedule type"
            required={false}
            optional={false}
            value={form.section_schedule_type}
            onChange={(v) => set({ section_schedule_type: v })}
            options={SCHEDULE_TYPES.map(({ value, label }) => ({ value, label }))}
            help={scheduleType?.help}
            hint="This is what decides how census and membership are calculated. Needed to publish."
          />
        </section>

        <section className="panel" aria-labelledby="schedule-heading">
          <h2 id="schedule-heading">Meeting schedule</h2>
          <p className="muted">
            Dates, times and locations, as they will be printed on the Official Attendance Record.
          </p>
          <MeetingScheduleEditor
            meetings={form.meeting_schedule}
            onChange={(meeting_schedule) => set({ meeting_schedule })}
          />
        </section>

        {versions.length > 0 && (
          <section className="panel panel--quiet" aria-labelledby="versions-heading">
            <h2 id="versions-heading">Published versions</h2>
            <p className="muted">
              Each one is a permanent snapshot. They are never edited — that is what makes a
              completed session's record stable.
            </p>
            <ul className="plain-list">
              {versions.map((v) => (
                <li key={v.version}>
                  <strong>Version {v.version}</strong>{' '}
                  <span className="muted">
                    · published {new Date(v.published_at).toLocaleString()}
                    {v.census_date && ` · census ${v.census_date}`}
                    {v.state_approved_hours && ` · ${v.state_approved_hours} approved hours`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="toolbar">
          <button type="submit" className="btn btn--primary" disabled={saving || publishing}>
            {saving ? 'Saving…' : 'Save draft'}
          </button>

          {!isNew && (
            <button
              type="button"
              className="btn"
              onClick={publish}
              disabled={saving || publishing}
            >
              {publishing ? 'Publishing…' : isPublished ? 'Publish a new version' : 'Publish section'}
            </button>
          )}

          <Link className="btn btn--quiet" to="/sections">
            Cancel
          </Link>
        </div>

        {!isNew && !canPublish && (
          <p className="muted">
            Your account can build and edit sections but not publish them — publishing needs the
            “class.publish” permission.
          </p>
        )}
        {isNew && (
          <p className="muted">
            Save the draft first. Publishing becomes available once the section exists, and it
            checks that the scheduled hours match the approved hours.
          </p>
        )}
      </form>
    </>
  );
}

export default SectionBuilderPage;
